import { Box, Typography } from "@mui/material";
import { ReactNode } from "react";

interface DateDisplayProps {
  date: string;
}

export const DateDisplay = ({ date }: DateDisplayProps) => (
  <Typography
    variant="body2"
    sx={{
      color: "#757575",
      fontSize: "0.875rem",
      fontWeight: 400,
    }}
  >
    {date}
  </Typography>
);

interface PointsBadgeProps {
  points: number | string;
  label: string;
  variant: "rewarded" | "spent" | "transferred-in" | "transferred-out";
}

export const PointsBadge = ({ points, label, variant }: PointsBadgeProps) => {
  const variantStyles = {
    rewarded: {
      bgcolor: "#e8f5e9",
      color: "#2e7d32",
    },
    spent: {
      bgcolor: "#ffebee",
      color: "#d32f2f",
    },
    "transferred-in": {
      bgcolor: "#e8f5e9",
      color: "#4caf50",
    },
    "transferred-out": {
      bgcolor: "#fff3e0",
      color: "#ff9800",
    },
  };

  const styles = variantStyles[variant];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "#757575",
          fontSize: "0.875rem",
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          bgcolor: styles.bgcolor,
          px: 2,
          py: 0.75,
          borderRadius: 2,
          minWidth: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: styles.color,
            fontWeight: 700,
            fontSize: "1.125rem",
          }}
        >
          {points}
        </Typography>
      </Box>
    </Box>
  );
};

interface DateAndPointsRowProps {
  date: string;
  points: number | string;
  label: string;
  variant: "rewarded" | "spent" | "transferred-in" | "transferred-out";
}

export const DateAndPointsRow = ({
  date,
  points,
  label,
  variant,
}: DateAndPointsRowProps) => (
  <Box display="flex" justifyContent="space-between" alignItems="center">
    <DateDisplay date={date} />
    <PointsBadge points={points} label={label} variant={variant} />
  </Box>
);

interface SectionDividerProps {
  children: ReactNode;
}

export const SectionDivider = ({ children }: SectionDividerProps) => (
  <Box
    sx={{
      borderTop: "1px solid #e0e0e0",
      pt: 2,
    }}
  >
    {children}
  </Box>
);

interface CenteredContentProps {
  children: ReactNode;
  minHeight?: number;
}

export const CenteredContent = ({
  children,
  minHeight = 80,
}: CenteredContentProps) => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    mb={1}
    minHeight={minHeight}
  >
    {children}
  </Box>
);
